;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { useEffect, useRef } from 'react';
import { getEcho } from '@/lib/echo';
import type {
  BatchTransferRealtimePayload,
  BatchTransferScaData,
} from '@/types/batch-transfer-realtime';

interface UseBatchTransferRealtimeOptions {
  userId: number | null;
  onProgress?: (percent: number) => void;
  onCompleted?: () => void;
  onError?: (message: string) => void;
  onSca?: (data: BatchTransferScaData) => void;
  enabled?: boolean;
}

/**
 * Subscribe to batch transfer realtime events via Reverb/Echo.
 * Listens on private channel `batch_transfers.{userId}` for progress milestones
 * and completion events.
 */
export function useBatchTransferRealtime({
  userId,
  onProgress,
  onCompleted,
  onError,
  onSca,
  enabled = true,
}: UseBatchTransferRealtimeOptions): void {
  const onProgressRef = useRef(onProgress);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  const onScaRef = useRef(onSca);
  onProgressRef.current = onProgress;
  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;
  onScaRef.current = onSca;

  useEffect(() => {
    if (!enabled || userId == null) {
      console.log('[Batch Transfer Realtime] Disabled or no userId', { enabled, userId });
      return;
    }

    const echo = getEcho();
    if (!echo) {
      console.log('[Batch Transfer Realtime] No Echo instance available');
      return;
    }

    console.log('[Batch Transfer Realtime] Subscribing to channel:', `batch_transfers.${userId}`);
    const channel = echo.private(`batch_transfers.${userId}`);

    const handler = (payload: BatchTransferRealtimePayload) => {
      console.log('[Batch Transfer Realtime] Received event:', payload);

      if (payload.type === 'success' && payload.message === 'sca' && payload.data) {
        console.log('[Batch Transfer Realtime] SCA event received:', payload.data);
        onScaRef.current?.(payload.data);
      } else if (payload.type === 'info') {
        const num = parseInt(payload.message, 10);
        if (!Number.isNaN(num)) {
          const percent = Math.min(100, Math.max(0, num));
          console.log('[Batch Transfer Realtime] Progress update:', percent);
          onProgressRef.current?.(percent);
        }
      } else if (
        payload.type === 'success' &&
        payload.message === 'Batch transfers finished successfully.'
      ) {
        console.log('[Batch Transfer Realtime] Batch transfer completed!');
        onProgressRef.current?.(100);
        onCompletedRef.current?.();
      } else if (payload.type === 'danger') {
        console.log('[Batch Transfer Realtime] Error received:', payload.message);
        onErrorRef.current?.(payload.message);
      }
    };

    channel.listen('.event', handler);

    return () => {
      console.log(
        '[Batch Transfer Realtime] Unsubscribing from channel:',
        `batch_transfers.${userId}`
      );
      channel.stopListening('.event');
      echo.leave(`batch_transfers.${userId}`);
    };
  }, [userId, enabled]);
}
